import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext.jsx';
import './NotificationCenter.css';

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'A l\'instant';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}

function NotificationCenter() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const panelElRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  // Desktop/tablet absolute dropdown: nudge back into the viewport if it
  // still overflows (narrow window ≥768, zoom, large text).
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = panelElRef.current;
    if (!el) return undefined;

    const clamp = () => {
      el.style.transform = '';
      const fixed = getComputedStyle(el).position === 'fixed';
      if (fixed) return; // insets already keep it on-screen
      const rect = el.getBoundingClientRect();
      const pad = 8;
      let shift = 0;
      if (rect.left < pad) shift += pad - rect.left;
      if (rect.right + shift > window.innerWidth - pad) {
        shift -= (rect.right + shift) - (window.innerWidth - pad);
      }
      el.style.transform = shift ? `translateX(${shift}px)` : '';
    };

    clamp();
    window.addEventListener('resize', clamp);
    return () => {
      window.removeEventListener('resize', clamp);
      if (el) el.style.transform = '';
    };
  }, [isOpen, notifications.length]);

  const handleNotificationClick = (notif) => {
    if (!notif.read_at) {
      markAsRead(notif.id);
    }
    const link = notif.data?.link;
    if (link) {
      navigate(link);
      setIsOpen(false);
    }
  };

  return (
    <div className="notification-center" ref={panelRef}>
      <button
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} non lues)` : ''}`}
        aria-expanded={isOpen}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-panel" ref={panelElRef} role="dialog" aria-label="Notifications">
          <div className="notification-panel-header">
            <h4>Notifications</h4>
            {unreadCount > 0 && (
              <button className="notification-mark-all" onClick={markAllAsRead}>
                Tout marquer lu
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <p>Aucune notification</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.id}
                  className={`notification-item ${!notif.read_at ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="notification-item-content">
                    <span className="notification-item-title">{notif.title}</span>
                    {notif.body && <span className="notification-item-body">{notif.body}</span>}
                    <span className="notification-item-time">{timeAgo(notif.created_at)}</span>
                  </div>
                  {!notif.read_at && <span className="notification-item-dot" />}
                </button>
              ))
            )}
          </div>

          <div className="notification-panel-footer">
            <button
              className="notification-prefs-link"
              onClick={() => { navigate('/notifications'); setIsOpen(false); }}
            >
              Préférences de notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
